import React, { useMemo, useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { sendEmail } from '../../store/thunks/leadsActions';
import request from '../../utils/request';
import {
  appendSignature,
  getSignature,
  stripTrackingHtml,
} from '../../utils/emailSignature';
import {
  buildMergeVars,
  detectUnresolvedVars,
  renderTemplate,
} from './workspace/utils';

export default function EmailComposer({
  leadId,
  lead = null,
  campaignLead = null,
  initialTo = '',
  subject: propSubject = '',
  threadId = null,
  campaignId = null,
  campaignLeadId = null,
  initialFrom = '',
  onClearTo,
  onSent,
}) {
  const dispatch = useDispatch();
  const adminInfo = useSelector((s) => s.login.adminInfo || {});
  const aliasOptions = Array.from(
    new Set(
      [
        initialFrom,
        adminInfo.gmail,
        adminInfo.gmailPrimaryAddress,
        adminInfo.email,
        ...(adminInfo.gmailAliases || []),
      ].filter(Boolean),
    ),
  );

  const ownerEmail = adminInfo?.gmailPrimaryAddress || adminInfo?.email || '';
  const [signatureHtml, setSignatureHtml] = useState(() => getSignature(ownerEmail));
  useEffect(() => {
    setSignatureHtml(getSignature(ownerEmail));
  }, [ownerEmail]);

  const [recipients, setRecipients] = useState(initialTo);
  const [fromAddress, setFromAddress] = useState(aliasOptions[0] || '');
  const [subject, setSubject] = useState(propSubject);
  const [body, setBody] = useState('');
  const [selectedTemplateId, setSelectedTemplateId] = useState('');
  const [includeSignature, setIncludeSignature] = useState(true);
  const [templates, setTemplates] = useState([]);
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const [forceAllow, setForceAllow] = useState(false);

  useEffect(() => {
    request
      .get('/templates', { withCredentials: true })
      .then((res) => setTemplates(res.data.templates || []))
      .catch(console.error);
  }, []);

  useEffect(() => {
    setRecipients(initialTo || '');
  }, [initialTo]);
  useEffect(() => {
    setSubject(propSubject || '');
  }, [propSubject]);
  useEffect(() => {
    if (aliasOptions[0]) {
      setFromAddress((current) => current || aliasOptions[0]);
    }
  }, [aliasOptions]);

  useEffect(() => {
    if (!selectedTemplateId) return;
    const tpl = templates.find((t) => t._id === selectedTemplateId);
    if (tpl) {
      setSubject(tpl.subject || '');
      setBody(tpl.html || '');
    }
  }, [selectedTemplateId, templates]);

  const mergeVars = useMemo(
    () => buildMergeVars({ lead, campaignLead, admin: adminInfo }),
    [lead, campaignLead, adminInfo],
  );

  const isRawText = (txt) => !/<[a-z][\s\S]*>/i.test(txt);

  const rawBodyHtml = useMemo(() => {
    return isRawText(body)
      ? body
          .split('\n')
          .map((line, i) => (i > 0 ? `<br/>${line}` : line))
          .join('')
      : body;
  }, [body]);

  const renderedSubject = useMemo(
    () => renderTemplate(subject, mergeVars),
    [subject, mergeVars],
  );

  const renderedBody = useMemo(
    () => renderTemplate(rawBodyHtml, mergeVars),
    [rawBodyHtml, mergeVars],
  );

  const unresolvedInBody = useMemo(
    () => detectUnresolvedVars(rawBodyHtml, mergeVars),
    [rawBodyHtml, mergeVars],
  );
  const unresolvedInSubject = useMemo(
    () => detectUnresolvedVars(subject, mergeVars),
    [subject, mergeVars],
  );
  const unresolved = Array.from(new Set([...unresolvedInBody, ...unresolvedInSubject]));

  const finalBodyHtml = useMemo(() => {
    const base = stripTrackingHtml(renderedBody);
    if (!includeSignature || !signatureHtml) return base;
    return appendSignature(base, signatureHtml);
  }, [renderedBody, includeSignature, signatureHtml]);

  const finalTextBody = useMemo(() => {
    const plain = renderTemplate(body, mergeVars);
    if (!includeSignature || !signatureHtml) return plain;
    const sigText = htmlToText(signatureHtml);
    return `${plain}\n\n--\n${sigText}`.trim();
  }, [body, mergeVars, includeSignature, signatureHtml]);

  const blockSend = unresolved.length > 0 && !forceAllow;

  const handleSend = () => {
    if (blockSend) return;
    setSending(true);

    dispatch(
      sendEmail({
        leadId,
        from: fromAddress,
        to: recipients,
        subject: renderedSubject,
        body: finalBodyHtml,
        text: finalTextBody,
        threadId,
        campaignId,
        campaignLeadId,
        signatureHtml: includeSignature ? signatureHtml : '',
        disableAutoSignature: true,
      }),
    )
      .unwrap()
      .then((result) => {
        setSent(true);
        setTimeout(() => setSent(false), 3000);
        setRecipients('');
        setSubject('');
        setBody('');
        setSelectedTemplateId('');
        setForceAllow(false);
        onClearTo?.();
        onSent?.({ status: 'contacted', result });
      })
      .catch(console.error)
      .finally(() => setSending(false));
  };

  return (
    <div className="mb-6 space-y-4">
      {sent && (
        <div className="rounded-[12px] border border-[#D1FAE5] bg-[#ECFDF5] px-4 py-2 text-sm text-[#10B981]">
          Message sent!
        </div>
      )}

      <div>
        {aliasOptions.length > 0 && (
          <div className="mb-3">
            <label className="mb-1 block text-sm font-semibold text-[#1F2937]">From</label>
            <select
              className="w-full rounded-[12px] border border-[#D1D5DB] p-3 focus:outline-none focus:ring-2 focus:ring-[#2563EB]"
              value={fromAddress}
              onChange={(e) => setFromAddress(e.target.value)}
            >
              {aliasOptions.map((alias) => (
                <option key={alias} value={alias}>
                  {alias}
                </option>
              ))}
            </select>
          </div>
        )}
        <label className="mb-1 block text-sm font-semibold text-[#1F2937]">Use Template</label>
        <select
          className="w-full rounded-[12px] border border-[#D1D5DB] p-3 focus:outline-none focus:ring-2 focus:ring-[#2563EB]"
          value={selectedTemplateId}
          onChange={(e) => setSelectedTemplateId(e.target.value)}
        >
          <option value="">Select a template</option>
          {templates.map((t) => (
            <option key={t._id} value={t._id}>
              {t.name || t.subject}
            </option>
          ))}
        </select>
      </div>

      <div>
        <label className="mb-1 block text-sm font-semibold text-[#1F2937]">To</label>
        <div className="flex">
          <input
            type="text"
            className="flex-1 rounded-l-[12px] border border-r-0 border-[#D1D5DB] p-3 focus:outline-none focus:ring-2 focus:ring-[#2563EB]"
            value={recipients}
            onChange={(e) => setRecipients(e.target.value)}
            placeholder="Recipient email"
          />
          {recipients && (
            <button
              onClick={() => {
                setRecipients('');
                onClearTo?.();
              }}
              className="rounded-r-[12px] border border-[#D1D5DB] bg-[#F9FAFB] px-4 py-2 text-[#EF4444] hover:bg-[#F3F4F6]"
            >
              ×
            </button>
          )}
        </div>
      </div>

      <div>
        <input
          type="text"
          className="w-full rounded-[12px] border border-[#D1D5DB] p-3 focus:outline-none focus:ring-2 focus:ring-[#2563EB]"
          placeholder="Subject"
          value={subject}
          onChange={(e) => setSubject(e.target.value)}
        />
      </div>

      <div>
        <textarea
          className="w-full rounded-[12px] border border-[#D1D5DB] p-3 focus:outline-none focus:ring-2 focus:ring-[#2563EB]"
          placeholder="Write your message. Use {{first_name|there}} for merge variables."
          rows={5}
          value={body}
          onChange={(e) => setBody(e.target.value)}
        />
      </div>

      <div className="flex flex-wrap items-center justify-between gap-2 rounded-[12px] border border-[#E5E7EB] bg-[#F9FAFB] px-3 py-2 text-xs text-[#374151]">
        <label className="inline-flex items-center gap-2">
          <input
            type="checkbox"
            checked={includeSignature}
            onChange={(e) => setIncludeSignature(e.target.checked)}
            className="h-3.5 w-3.5 rounded border-zinc-300 text-indigo-600"
          />
          <span>
            Append my signature
            {!signatureHtml ? (
              <span className="ml-1 text-[#EF4444]">
                (not set — add one in Settings → Email signature)
              </span>
            ) : null}
          </span>
        </label>
        <span className="text-[11px] text-[#6B7280]">
          Tracking pixels are stripped automatically.
        </span>
      </div>

      {unresolved.length > 0 && (
        <div className="rounded-[12px] border border-amber-200 bg-amber-50 px-3 py-2 text-xs leading-5 text-amber-900">
          <p className="font-semibold">
            {unresolved.length === 1
              ? "1 merge variable is empty for this lead:"
              : `${unresolved.length} merge variables are empty for this lead:`}
          </p>
          <p className="mt-0.5">
            {unresolved.map((key) => (
              <code
                key={key}
                className="mr-1 rounded bg-amber-100 px-1 py-0.5 font-mono text-[11px] text-amber-900"
              >{`{{${key}}}`}</code>
            ))}
          </p>
          <p className="mt-1">
            Add a fallback like <code className="rounded bg-white px-1 py-0.5 font-mono text-[11px]">{`{{first_name|there}}`}</code> or fill the lead's data.{" "}
            <button
              type="button"
              onClick={() => setForceAllow((v) => !v)}
              className="font-semibold underline"
            >
              {forceAllow ? "Re-enable safety check" : "Send anyway"}
            </button>
          </p>
        </div>
      )}

      {(body || includeSignature) && (
        <div className="rounded-[12px] border border-[#E5E7EB] bg-white p-4 shadow-sm">
          <h4 className="mb-2 text-sm font-semibold text-[#1F2937]">Live preview</h4>
          <div
            className="prose prose-sm max-w-none whitespace-pre-line text-sm text-[#374151]"
            dangerouslySetInnerHTML={{ __html: finalBodyHtml }}
          />
        </div>
      )}

      <button
        onClick={handleSend}
        disabled={sending || blockSend}
        className={`w-full rounded-[12px] px-4 py-3 text-center font-medium text-white transition-colors duration-150 ${
          sending || blockSend
            ? 'cursor-not-allowed bg-[#9CA3AF]'
            : 'bg-[#2563EB] hover:bg-[#1D4ED8]'
        }`}
      >
        {sending ? 'Sending…' : blockSend ? 'Fix merge variables to send' : 'Send'}
      </button>
    </div>
  );
}

function htmlToText(html) {
  if (!html) return '';
  if (typeof document !== 'undefined') {
    const tmp = document.createElement('div');
    tmp.innerHTML = html;
    return (tmp.textContent || tmp.innerText || '').replace(/\s+\n/g, '\n').trim();
  }
  return html.replace(/<[^>]+>/g, ' ').replace(/\s+/g, ' ').trim();
}
